import { BlogArticle } from '../types';

// 香港學生常見英文錯誤 → CTA: /module/writing
const article: BlogArticle = {
  slug: 'common-english-mistakes-hk-students',
  date: '2026-09-18',
  readingMinutes: 5,
  tags: ['英文', '作文', '常見錯誤', 'DSE'],

  locales: {
    zhHant: {
      title: '香港學生最常犯的 8 個英文錯誤',
      excerpt: '「Open the light」其實係錯？呢 8 個港式英文錯誤，改咗你嘅作文即刻升一個 grade。',
      keywords: '英文錯誤, 港式英文, Chinglish, 英文作文, DSE English',
      contentHtml: `
        <p>好多香港學生英文文法底子唔差，但作文成日俾人扣啲「唔知點解錯」嘅分。以下 8 個錯誤，係批改過大量港生作文後最常見嘅 —— 睇吓你中咗幾多個。</p>

        <h2>1. 動詞直譯中文</h2>
        <p>「開燈」唔係 <s>open the light</s>，係 <strong>turn on the light</strong>。「學習知識」唔係 <s>learn knowledge</s>，係 <strong>gain/acquire knowledge</strong>。英文動詞配搭（collocation）唔可以直譯。</p>

        <h2>2. 主謂一致（Subject-verb agreement）</h2>
        <p><s>He go to school.</s> → <strong>He goes to school.</strong> 第三人稱單數加 s，係最基本但最多人漏嘅位。</p>

        <h2>3. 時態混亂</h2>
        <p>一段入面時態跳嚟跳去係大忌。記敘文就用過去式，議論文講普遍事實就用現在式，全篇要一致。</p>

        <h2>4. 冠詞 a / an / the</h2>
        <p>中文冇冠詞，所以港生成日漏。<s>I am student.</s> → <strong>I am a student.</strong> 可數名詞單數前面一定要有冠詞。</p>

        <h2>5. 可數 vs 不可數名詞</h2>
        <p><s>many informations</s> → <strong>much information</strong>。<s>an advice</s> → <strong>some advice</strong>。information、advice、homework、equipment 都係不可數。</p>

        <h2>6. 介詞錯配</h2>
        <p><s>listen music</s> → <strong>listen to music</strong>。<s>discuss about</s> → <strong>discuss</strong>（唔使加 about）。介詞冇邏輯可言，唯有逐個記。</p>

        <h2>7. 中式句式</h2>
        <p>「我哋應該保護環境」直譯 <s>We should protect the environment.</s> 其實冇錯，但成篇都係 "We should..." 就會好單調。試吓用 <strong>It is important for us to...</strong> 或者 <strong>There is a need to...</strong> 變化句式。</p>

        <h2>8. 過度使用 very</h2>
        <p><s>very good</s> → <strong>excellent</strong>；<s>very tired</s> → <strong>exhausted</strong>。一個精準嘅形容詞，好過十個 very。</p>

        <h2>錯誤速查表</h2>
        <table>
          <thead>
            <tr><th>❌ 錯</th><th>✅ 啱</th></tr>
          </thead>
          <tbody>
            <tr><td>open the light</td><td>turn on the light</td></tr>
            <tr><td>He go</td><td>He goes</td></tr>
            <tr><td>many informations</td><td>much information</td></tr>
            <tr><td>discuss about</td><td>discuss</td></tr>
            <tr><td>very good</td><td>excellent</td></tr>
          </tbody>
        </table>

        <div class="tip-box">
          <strong>💡 貼士：</strong>想知道自己作文中咗邊啲？用 AI 作文批改，幾秒就列出你嘅錯誤清單同修訂建議。
        </div>

        <p><a class="blog-cta-button" href="/module/writing">✍️ 免費批改我的英文作文 →</a></p>
      `,
    },

    zhHans: {
      title: '香港学生最常犯的 8 个英文错误',
      excerpt: '「Open the light」其实是错的？这 8 个港式英文错误，改了你的作文立刻升一个等级。',
      keywords: '英文错误, 港式英文, Chinglish, 英文作文, DSE English',
      contentHtml: `
        <p>很多香港学生英文语法底子不差，但作文总被扣一些「不知道为什么错」的分。以下 8 个错误，是批改过大量港生作文后最常见的 —— 看看你中了几个。</p>

        <h2>1. 动词直译中文</h2>
        <p>「开灯」不是 <s>open the light</s>，是 <strong>turn on the light</strong>。「学习知识」不是 <s>learn knowledge</s>，是 <strong>gain/acquire knowledge</strong>。英文动词搭配（collocation）不能直译。</p>

        <h2>2. 主谓一致（Subject-verb agreement）</h2>
        <p><s>He go to school.</s> → <strong>He goes to school.</strong> 第三人称单数加 s，是最基本但最多人漏的地方。</p>

        <h2>3. 时态混乱</h2>
        <p>一段里面时态跳来跳去是大忌。记叙文就用过去式，议论文讲普遍事实就用现在式，全篇要一致。</p>

        <h2>4. 冠词 a / an / the</h2>
        <p>中文没有冠词，所以港生经常漏。<s>I am student.</s> → <strong>I am a student.</strong> 可数名词单数前面一定要有冠词。</p>

        <h2>5. 可数 vs 不可数名词</h2>
        <p><s>many informations</s> → <strong>much information</strong>。<s>an advice</s> → <strong>some advice</strong>。information、advice、homework、equipment 都是不可数。</p>

        <h2>6. 介词错配</h2>
        <p><s>listen music</s> → <strong>listen to music</strong>。<s>discuss about</s> → <strong>discuss</strong>（不用加 about）。介词没有逻辑可言，只能逐个记。</p>

        <h2>7. 中式句式</h2>
        <p>「我们应该保护环境」直译 <s>We should protect the environment.</s> 其实没错，但整篇都是 "We should..." 就会很单调。试试用 <strong>It is important for us to...</strong> 或 <strong>There is a need to...</strong> 变换句式。</p>

        <h2>8. 过度使用 very</h2>
        <p><s>very good</s> → <strong>excellent</strong>；<s>very tired</s> → <strong>exhausted</strong>。一个精准的形容词，好过十个 very。</p>

        <h2>错误速查表</h2>
        <table>
          <thead>
            <tr><th>❌ 错</th><th>✅ 对</th></tr>
          </thead>
          <tbody>
            <tr><td>open the light</td><td>turn on the light</td></tr>
            <tr><td>He go</td><td>He goes</td></tr>
            <tr><td>many informations</td><td>much information</td></tr>
            <tr><td>discuss about</td><td>discuss</td></tr>
            <tr><td>very good</td><td>excellent</td></tr>
          </tbody>
        </table>

        <div class="tip-box">
          <strong>💡 提示：</strong>想知道自己作文中了哪些？用 AI 作文批改，几秒就列出你的错误清单和修改建议。
        </div>

        <p><a class="blog-cta-button" href="/module/writing">✍️ 免费批改我的英文作文 →</a></p>
      `,
    },

    en: {
      title: '8 English Mistakes Hong Kong Students Make Most',
      excerpt: '"Open the light" is wrong — and 7 more classic HK-style errors. Fix these and your essays jump a grade instantly.',
      keywords: 'English mistakes, Chinglish, essay writing, DSE English, common errors',
      contentHtml: `
        <p>Many Hong Kong students have solid grammar foundations, yet keep losing marks on mistakes they don't even notice. These are the 8 most common errors seen in HK student essays — how many do you make?</p>

        <h2>1. Verbs translated directly from Chinese</h2>
        <p>"開燈" is not <s>open the light</s> — it's <strong>turn on the light</strong>. "學習知識" is not <s>learn knowledge</s> — it's <strong>gain knowledge</strong>. English collocations can't be translated word by word.</p>

        <h2>2. Subject-verb agreement</h2>
        <p><s>He go to school.</s> → <strong>He goes to school.</strong> The third-person "s" is basic, yet it's the most frequently dropped mark.</p>

        <h2>3. Tense inconsistency</h2>
        <p>Jumping between tenses within a paragraph is a cardinal sin. Narratives stay in past tense; general facts in essays stay in present. Keep it consistent.</p>

        <h2>4. Articles: a / an / the</h2>
        <p>Chinese has no articles, so they get dropped constantly. <s>I am student.</s> → <strong>I am a student.</strong> A singular countable noun always needs an article.</p>

        <h2>5. Countable vs uncountable nouns</h2>
        <p><s>many informations</s> → <strong>much information</strong>. <s>an advice</s> → <strong>some advice</strong>. Information, advice, homework and equipment are all uncountable.</p>

        <h2>6. Preposition mismatches</h2>
        <p><s>listen music</s> → <strong>listen to music</strong>. <s>discuss about</s> → just <strong>discuss</strong>. Prepositions follow no logic — memorise them one by one.</p>

        <h2>7. Chinglish sentence patterns</h2>
        <p>Starting every sentence with "We should..." isn't wrong, but it's monotonous. Vary with <strong>It is important for us to...</strong> or <strong>There is a need to...</strong>.</p>

        <h2>8. Overusing "very"</h2>
        <p><s>very good</s> → <strong>excellent</strong>; <s>very tired</s> → <strong>exhausted</strong>. One precise adjective beats ten "very"s.</p>

        <h2>Quick reference</h2>
        <table>
          <thead>
            <tr><th>❌ Wrong</th><th>✅ Right</th></tr>
          </thead>
          <tbody>
            <tr><td>open the light</td><td>turn on the light</td></tr>
            <tr><td>He go</td><td>He goes</td></tr>
            <tr><td>many informations</td><td>much information</td></tr>
            <tr><td>discuss about</td><td>discuss</td></tr>
            <tr><td>very good</td><td>excellent</td></tr>
          </tbody>
        </table>

        <div class="tip-box">
          <strong>💡 Tip:</strong> Want to know which of these sneak into your essays? AI essay grading lists your mistakes and suggests revisions in seconds.
        </div>

        <p><a class="blog-cta-button" href="/module/writing">✍️ Grade my English essay for free →</a></p>
      `,
    },
  },
};

export default article;
